/**
 * ChatHeader Component - HYB Shop Chat
 * Page header with WebSocket connection status and refresh action
 *
 * Design: Warm professional header with status indicators
 */


import { MessageSquare, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../ui/PageHeader';
import { cn } from '../../../utils/responsive';

type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface ChatHeaderProps {
  /** WebSocket connection state */
  isConnected: boolean;
  /** Detailed WebSocket status */
  wsStatus?: WebSocketStatus;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Whether refresh is in progress */
  isRefreshing?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get status configuration based on connection state
 */
function getStatusConfig(isConnected: boolean, wsStatus?: WebSocketStatus) {
  if (isConnected) {
    return {
      label: 'เชื่อมต่อแล้ว',
      icon: Wifi,
      colors: cn(
        'bg-emerald-50 text-emerald-700 ring-emerald-200/50',
        'dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800/50'
      ),
      dotColor: 'bg-emerald-500',
      pulse: true,
    };
  }

  if (wsStatus === 'connecting') {
    return {
      label: 'กำลังเชื่อมต่อ...',
      icon: Wifi,
      colors: cn(
        'bg-amber-50 text-amber-700 ring-amber-200/50',
        'dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800/50'
      ),
      dotColor: 'bg-amber-500',
      pulse: true,
    };
  }

  return {
    label: 'ออฟไลน์',
    icon: WifiOff,
    colors: cn(
      'bg-red-50 text-red-700 ring-red-200/50',
      'dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800/50'
    ),
    dotColor: 'bg-red-500',
    pulse: false,
  };
}

export function ChatHeader({
  isConnected,
  wsStatus,
  onRefresh,
  isRefreshing = false,
  className,
}: ChatHeaderProps) {
  const status = getStatusConfig(isConnected, wsStatus);
  const StatusIcon = status.icon;

  return (
    <PageHeader
      title="การจัดการแชท"
      description="ดูและตอบกลับข้อความจากลูกค้าแบบเรียลไทม์"
      leading={<MessageSquare className="h-5 w-5" />}
      className={className}
      actions={
        <div className="flex items-center gap-2 lg:gap-3">
          {/* Connection Status Indicator */}
          <div
            className={cn(
              'flex items-center gap-1.5 lg:gap-2',
              'rounded-full px-2.5 lg:px-3 py-1.5',
              'text-[10px] lg:text-xs font-medium',
              'ring-1 ring-inset',
              'transition-all duration-300',
              status.colors
            )}
            role="status"
            aria-live="polite"
            aria-label={`สถานะการเชื่อมต่อ: ${status.label}`}
          >
            {/* Status Dot */}
            <span className="relative flex h-2 w-2">
              {status.pulse && (
                <span
                  className={cn(
                    'absolute inline-flex h-full w-full rounded-full opacity-75',
                    'animate-ping',
                    status.dotColor
                  )}
                  aria-hidden="true"
                />
              )}
              <span
                className={cn(
                  'relative inline-flex h-2 w-2 rounded-full',
                  status.dotColor
                )}
              />
            </span>

            {/* Icon */}
            <StatusIcon className="h-3 w-3" aria-hidden="true" />

            {/* Label - Hidden on very small screens */}
            <span className="hidden sm:inline">{status.label}</span>
          </div>

          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={cn(
              'inline-flex items-center gap-2',
              'rounded-xl border border-stone-200 dark:border-gray-700',
              'bg-white dark:bg-gray-800',
              'px-3 lg:px-4 py-2',
              'text-sm font-medium text-stone-700 dark:text-gray-200',
              'shadow-sm',
              'transition-all duration-200',
              // Hover states
              'hover:bg-stone-50 dark:hover:bg-gray-700',
              'hover:border-amber-400 dark:hover:border-amber-500',
              'hover:shadow-accent',
              // Active state
              'active:scale-[0.98]',
              // Focus state
              'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2',
              'dark:focus:ring-offset-gray-900',
              // Disabled state
              'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-sm',
              // Touch-friendly
              'touch-action-manipulation min-h-[44px]'
            )}
            title="รีเฟรชรายการสนทนา"
            aria-label="รีเฟรชรายการสนทนา"
          >
            <RefreshCw
              className={cn(
                'h-4 w-4',
                'transition-transform duration-500',
                isRefreshing && 'animate-spin'
              )}
              aria-hidden="true"
            />
            <span className="hidden sm:inline">รีเฟรช</span>
          </button>
        </div>
      }
    />
  );
}

export default ChatHeader;
